import { Icon } from '@iconify/react/dist/iconify.js';
import { Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { useTableOfContext } from '@/contexts/TableOfContentContext';
import { cn } from '@/lib/utils';
import { useScrollBody } from '@/contexts/ScrollBodyContext';
import { useTableOfContentsHighlighting } from '@/hooks/components/useTableOfContentsHighlighting';

interface TableOfContentsProps {
  numberingStyle?: 'hierarchical' | 'sequential' | 'none';
  className?: string;
}

// Padding levels for different heading depths
const paddingMap = {
  1: 'ml-0',
  2: 'ml-2',
  3: 'ml-4',
  4: 'ml-6',
};

export const TableOfContents = ({
  numberingStyle = 'none',
  className,
}: TableOfContentsProps) => {
  const { items, activeItemId, setActiveItem } = useTableOfContext();
  const { scrollTo, viewportRef } = useScrollBody();

  const { setNavigatingToItemId } = useTableOfContentsHighlighting({
    items,
    viewportRef,
    setActiveItem,
  });

  const getHierarchicalNumber = (index: number, level: number) => {
    // For top level items (level 1), just count how many level 1 items came before
    if (level === 1) {
      const sectionCount = items
        .slice(0, index + 1)
        .filter((item) => item.level === 1).length;
      return `${sectionCount}`;
    }

    // Find which main section (level 1) we're currently in
    const currentSection = items
      .slice(0, index + 1)
      .filter((item) => item.level === 1).length;

    // Count subsections at the current level within the current main section
    const subSections = items.slice(0, index + 1).filter((item) => {
      // Find which main section this item belongs to
      const itemSection = items
        .slice(0, items.indexOf(item))
        .filter((i) => i.level === 1).length;
      // Only count items at our level that belong to our current section
      return item.level === level && itemSection === currentSection;
    }).length;

    // Build the hierarchical number (e.g., 2.3.1) based on level depth
    return Array.from({ length: level }, (_, i) =>
      i === 0 ? currentSection : subSections,
    ).join('.');
  };

  const getItemNumber = (index: number, level: number) => {
    if (numberingStyle === 'none') return '';
    if (numberingStyle === 'sequential') return `${index + 1}. `;
    return `${getHierarchicalNumber(index, level)}. `;
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    scrollTo(id);
    setActiveItem(id);
    setNavigatingToItemId(id);
  };

  return (
    <nav
      className={cn('sticky top-[20px] w-[250px]', className)}
      aria-label="Table of contents"
    >
      <Text size="base" bold>
        Table of contents
      </Text>

      <Spacer size="lg" />

      <ul className="list-none p-0">
        {items.map((item, index) => (
          <li
            key={item.id}
            className={cn(
              paddingMap[item.level as 1 | 2 | 3 | 4],
              'flex items-center justify-start gap-2',
            )}
          >
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={cn(
                'block max-w-[200px] overflow-hidden text-ellipsis py-1.5 text-sm no-underline transition-colors duration-200 hover:text-blue-600 dark:hover:text-blue-400',
                !!item.error && 'text-red-600 dark:text-red-500',
                !item.error && 'text-gray-500 dark:text-gray-400',
                activeItemId === item.id &&
                  'font-medium text-blue-600 underline dark:text-blue-400',
              )}
            >
              {getItemNumber(index, item.level)}
              {item.displayName}
            </a>
            {!!item.error && (
              <Icon
                icon="ph:warning-octagon-bold"
                height="1.2rem"
                width="1.2rem"
                className="shrink-0 text-red-600 dark:text-red-500"
              />
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
